/**
 * PortalLN360Test — Integration test page for IntentDrive -> LeadNexus360 pipeline
 *
 * Sections:
 *  1. Connection Config — base URL, supplier key, campaign slug (persisted to localStorage)
 *  2. Send Test Lead — pre-filled lead form, proxied through webhook-test Edge Function
 *  3. Integration Status — last 5 delivery_logs for destination_type='leadnexus'
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
  RefreshCw,
  Wifi,
  WifiOff,
  Save,
} from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';

// ─── Constants ────────────────────────────────────────────

const STORAGE_KEY = 'intentdrive_ln360_config';

const SUPABASE_URL =
  import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

// ─── Types ────────────────────────────────────────────────

interface LN360Config {
  base_url: string;
  supplier_api_key: string;
  campaign_slug: string;
}

interface TestLeadForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  state: string;
  zip: string;
  vertical: string;
  credit_tier: string;
}

interface WebhookTestResponse {
  status_code: number;
  response_body: string;
  latency_ms: number;
  error?: string;
}

interface DeliveryLog {
  id: string;
  created_at: string;
  destination_type: string;
  status: string;
  response_body: string | null;
  latency_ms: number | null;
  lead_id: string | null;
}

// ─── Helpers ──────────────────────────────────────────────

function loadConfig(): LN360Config {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { base_url: '', supplier_api_key: '', campaign_slug: '' };
}

function saveConfig(config: LN360Config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

async function callWebhookTest(url: string, payload: unknown): Promise<WebhookTestResponse> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/webhook-test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${SUPABASE_KEY}`,
      apikey: SUPABASE_KEY,
    },
    body: JSON.stringify({ url, payload }),
  });
  if (!res.ok) throw new Error(`webhook-test returned HTTP ${res.status}`);
  return res.json();
}

// ─── Sub-components ───────────────────────────────────────

function SectionCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        <Icon className="h-4 w-4 text-emerald-400" />
        <h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function StatusBadge({ success }: { success: boolean }) {
  return success ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <CheckCircle2 className="h-3 w-3" /> Success
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
      <XCircle className="h-3 w-3" /> Failed
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────

export default function PortalLN360Test() {
  // ── Config state ──
  const [config, setConfig] = useState<LN360Config>(loadConfig);
  const [configSaved, setConfigSaved] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // ── Test lead state ──
  const [leadForm, setLeadForm] = useState<TestLeadForm>({
    first_name: 'John',
    last_name: 'Doe',
    email: 'john.doe@test.com',
    phone: '5551234567',
    state: 'FL',
    zip: '33101',
    vertical: 'debt_relief',
    credit_tier: 'fair',
  });
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'done'>('idle');
  const [sendResult, setSendResult] = useState<WebhookTestResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // ── Delivery logs state ──
  const [logs, setLogs] = useState<DeliveryLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // ── Load delivery logs ──
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await supabaseGet<DeliveryLog[]>(
        'delivery_logs?destination_type=eq.leadnexus&order=created_at.desc&limit=5&select=id,created_at,destination_type,status,response_body,latency_ms,lead_id'
      );
      setLogs(data);
    } catch (err: any) {
      setLogsError(err?.message || 'Failed to fetch delivery logs');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // ── Config handlers ──
  const handleSaveConfig = () => {
    saveConfig(config);
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
  };

  const handleTestConnection = async () => {
    if (!config.base_url) return;
    setConnectionStatus('testing');
    setConnectionError(null);
    try {
      const healthUrl = `${config.base_url.replace(/\/$/, '')}/health`;
      const res = await callWebhookTest(healthUrl, {});
      if (res.status_code >= 200 && res.status_code < 400) {
        setConnectionStatus('ok');
      } else {
        setConnectionStatus('error');
        setConnectionError(`Health check returned HTTP ${res.status_code}: ${res.response_body?.substring(0, 200)}`);
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setConnectionError(err?.message || 'Connection test failed');
    }
  };

  // ── Send test lead handler ──
  const handleSendTestLead = async () => {
    if (!config.base_url || !config.supplier_api_key || !config.campaign_slug) {
      setSendError('Please fill in all connection config fields first');
      return;
    }
    setSendStatus('sending');
    setSendResult(null);
    setSendError(null);
    try {
      const ingestUrl = `${config.base_url.replace(/\/$/, '')}/v1/ingest`;
      const payload = {
        campaign_slug: config.campaign_slug,
        supplier_api_key: config.supplier_api_key,
        test_mode: true,
        payload: {
          ...leadForm,
          source: 'intentdrive_test',
          session_id: `test_${Date.now()}`,
        },
      };
      const result = await callWebhookTest(ingestUrl, payload);
      setSendResult(result);
      setSendStatus('done');
      // Refresh logs after sending
      setTimeout(() => fetchLogs(), 1500);
    } catch (err: any) {
      setSendError(err?.message || 'Failed to send test lead');
      setSendStatus('done');
    }
  };

  // ── Parse send result for display ──
  let parsedResponse: Record<string, unknown> | null = null;
  if (sendResult?.response_body) {
    try {
      parsedResponse = JSON.parse(sendResult.response_body);
    } catch { /* not JSON */ }
  }

  return (
    <PortalLayout>
      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Plug className="h-6 w-6 text-emerald-400" />
            LeadNexus360 Integration Test
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Test the IntentDrive to LeadNexus360 pipeline end-to-end
          </p>
        </div>

        {/* ── Section 1: Connection Config ── */}
        <SectionCard title="Connection Config" icon={Wifi}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">LN360 API URL</label>
                <input
                  type="text"
                  value={config.base_url}
                  onChange={(e) => setConfig((c) => ({ ...c, base_url: e.target.value }))}
                  placeholder="https://api.leadnexus360.com"
                  className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Supplier API Key</label>
                <input
                  type="password"
                  value={config.supplier_api_key}
                  onChange={(e) => setConfig((c) => ({ ...c, supplier_api_key: e.target.value }))}
                  placeholder="sk_..."
                  className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Campaign Slug</label>
                <input
                  type="text"
                  value={config.campaign_slug}
                  onChange={(e) => setConfig((c) => ({ ...c, campaign_slug: e.target.value }))}
                  placeholder="debt-relief-v1"
                  className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveConfig}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white hover:bg-slate-700 transition-colors"
              >
                <Save className="h-3.5 w-3.5" />
                {configSaved ? 'Saved!' : 'Save Config'}
              </button>
              <button
                onClick={handleTestConnection}
                disabled={!config.base_url || connectionStatus === 'testing'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-sm text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {connectionStatus === 'testing' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : connectionStatus === 'ok' ? (
                  <Wifi className="h-3.5 w-3.5" />
                ) : connectionStatus === 'error' ? (
                  <WifiOff className="h-3.5 w-3.5" />
                ) : (
                  <Wifi className="h-3.5 w-3.5" />
                )}
                Test Connection
              </button>
              {connectionStatus === 'ok' && (
                <span className="text-xs text-emerald-400">Server is reachable</span>
              )}
              {connectionStatus === 'error' && connectionError && (
                <span className="text-xs text-red-400 max-w-md truncate">{connectionError}</span>
              )}
            </div>
          </div>
        </SectionCard>

        {/* ── Section 2: Send Test Lead ── */}
        <SectionCard title="Send Test Lead" icon={Send}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                [
                  ['first_name', 'First Name'],
                  ['last_name', 'Last Name'],
                  ['email', 'Email'],
                  ['phone', 'Phone'],
                  ['state', 'State'],
                  ['zip', 'Zip'],
                  ['vertical', 'Vertical'],
                  ['credit_tier', 'Credit Tier'],
                ] as [keyof TestLeadForm, string][]
              ).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-xs text-slate-400 mb-1">{label}</label>
                  <input
                    type="text"
                    value={leadForm[field]}
                    onChange={(e) =>
                      setLeadForm((f) => ({ ...f, [field]: e.target.value }))
                    }
                    className="w-full bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSendTestLead}
                disabled={sendStatus === 'sending'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-sm text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {sendStatus === 'sending' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                {sendStatus === 'sending' ? 'Sending...' : 'Send to LeadNexus360'}
              </button>
              <span className="text-xs text-slate-500">test_mode: true</span>
            </div>

            {sendError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                {sendError}
              </div>
            )}

            {sendResult && (
              <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-4 space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <StatusBadge success={sendResult.status_code >= 200 && sendResult.status_code < 300} />
                  <span className="text-slate-400">
                    HTTP {sendResult.status_code} &middot; {sendResult.latency_ms}ms
                  </span>
                </div>

                {parsedResponse && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {parsedResponse.lead_id && (
                      <div>
                        <span className="text-slate-500 text-xs">Lead ID</span>
                        <p className="text-white font-mono text-xs">{String(parsedResponse.lead_id)}</p>
                      </div>
                    )}
                    {parsedResponse.status && (
                      <div>
                        <span className="text-slate-500 text-xs">Status</span>
                        <p className={`font-semibold text-xs ${parsedResponse.status === 'SOLD' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {String(parsedResponse.status)}
                        </p>
                      </div>
                    )}
                    {parsedResponse.accepted_buyers != null && (
                      <div>
                        <span className="text-slate-500 text-xs">Accepted Buyers</span>
                        <p className="text-white text-xs">{String(parsedResponse.accepted_buyers)}</p>
                      </div>
                    )}
                    {parsedResponse.total_latency_ms != null && (
                      <div>
                        <span className="text-slate-500 text-xs">LN360 Latency</span>
                        <p className="text-white text-xs">{String(parsedResponse.total_latency_ms)}ms</p>
                      </div>
                    )}
                  </div>
                )}

                <details className="text-xs">
                  <summary className="text-slate-500 cursor-pointer hover:text-slate-300">
                    Raw response
                  </summary>
                  <pre className="mt-2 p-2 rounded bg-slate-900 text-slate-300 overflow-x-auto max-h-40">
                    {sendResult.response_body}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </SectionCard>

        {/* ── Section 3: Integration Status ── */}
        <SectionCard title="Integration Status" icon={RefreshCw}>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Last 5 delivery logs where destination_type = leadnexus
              </p>
              <button
                onClick={fetchLogs}
                disabled={logsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${logsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {logsError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                {logsError}
              </div>
            )}

            {logs.length === 0 && !logsLoading && !logsError && (
              <p className="text-sm text-slate-500 text-center py-6">
                No LeadNexus delivery logs found yet
              </p>
            )}

            {logs.map((log) => {
              const isSuccess = log.status === 'success' || log.status === 'delivered';
              let responsePreview: string | null = null;
              if (log.response_body) {
                try {
                  responsePreview = JSON.stringify(JSON.parse(log.response_body), null, 2);
                } catch {
                  responsePreview = log.response_body;
                }
              }

              return (
                <div
                  key={log.id}
                  className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusBadge success={isSuccess} />
                      <span className="text-xs text-slate-400 font-mono">
                        {log.lead_id ? log.lead_id.substring(0, 8) + '...' : 'n/a'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {log.latency_ms != null && <span>{log.latency_ms}ms</span>}
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {responsePreview && (
                    <details className="text-xs">
                      <summary className="text-slate-500 cursor-pointer hover:text-slate-300">
                        Response body
                      </summary>
                      <pre className="mt-1 p-2 rounded bg-slate-900 text-slate-300 overflow-x-auto max-h-32">
                        {responsePreview.substring(0, 500)}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    </PortalLayout>
  );
}
